import { Button, Dialog, DialogContent, Stack, Typography } from '@mui/material';
import { DialogChromeFooter, DialogChromeHeader } from 'components/atoms/Dialog/DialogChrome';
import Loader from 'components/Loader';
import React from 'react';
import { useClipboard } from 'use-clipboard-copy';

export interface ExportProgress {
  stage: string;
  current?: number;
  total?: number;
}

export type ExportOperationStatus = 'running' | 'success' | 'error' | 'cancelled';

export interface ExportProgressFallback {
  label: string;
  message: string;
  report?: string;
}

interface ExportProgressDialogProps {
  open: boolean;
  title: string;
  status: ExportOperationStatus;
  progress: ExportProgress;
  error?: string;
  // An alternative offered with the error, for example downloading the original file.
  fallback?: ExportProgressFallback;
  canCancel: boolean;
  onCancel: () => void;
  onClose: () => void;
  onRetry: () => void;
  onFallback?: () => void;
}

const statusText = (status: ExportOperationStatus, progress: ExportProgress, error?: string) => {
  if (status === 'success') return 'Export is ready.';
  if (status === 'cancelled') return 'Export cancelled.';
  if (status === 'error') return error || 'Export failed.';
  return progress.stage;
};

const ExportProgressDialog: React.FC<ExportProgressDialogProps> = ({
  open,
  title,
  status,
  progress,
  error,
  fallback,
  canCancel,
  onCancel,
  onClose,
  onRetry,
  onFallback,
}) => {
  const clipboard = useClipboard({ copiedTimeout: 2000 });
  const determinate = progress.total !== undefined && progress.current !== undefined;
  const progressValue = determinate
    ? Math.min(100, (progress.current! / Math.max(1, progress.total!)) * 100)
    : undefined;
  const showFallback = status === 'error' && Boolean(fallback) && Boolean(onFallback);

  return (
    <Dialog open={open} onClose={status === 'running' ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogChromeHeader
        title={title}
        onClose={status === 'running' && canCancel ? onCancel : onClose}
        closeButtonAriaLabel={status === 'running' && canCancel ? 'Cancel export' : 'Close export progress'}
      />
      <DialogContent dividers>
        {status === 'running' ? (
          <Loader
            inline
            message={statusText(status, progress, error)}
            value={progressValue}
            ariaLabel="Export progress"
          />
        ) : (
          <Stack spacing={1.5} alignItems="flex-start">
            <Typography role="status" aria-live="polite">
              {statusText(status, progress, error)}
            </Typography>
            {showFallback && fallback && (
              <>
                <Typography variant="body2">{fallback.message}</Typography>
                {fallback.report && (
                  <Button
                    size="small"
                    color="inherit"
                    variant="outlined"
                    onClick={() => clipboard.copy(fallback.report)}
                  >
                    {clipboard.copied ? 'Copied' : 'Copy error details'}
                  </Button>
                )}
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogChromeFooter>
        {status === 'running' && canCancel && <Button onClick={onCancel}>Cancel</Button>}
        {showFallback && fallback && (
          <Button variant="outlined" color="secondary" onClick={onFallback}>
            {fallback.label}
          </Button>
        )}
        {status === 'error' && <Button onClick={onRetry}>Retry</Button>}
        {status !== 'running' && <Button onClick={onClose}>Close</Button>}
      </DialogChromeFooter>
    </Dialog>
  );
};

export default ExportProgressDialog;
